import {
  AppActionType,
  SET_FACTORING_APPLICATION,
  SET_FACTORING_SECTION,
  SET_VERIFIED_DOT,
  ADD_ADDITIONAL_OWNER,
  REMOVE_ADDITIONAL_OWNER,
  SET_LEGAL_TEXTS,
  SET_SUBMISSION_STATUS,
  SET_DOT_CHECK_RESPONSE,
  SET_BRANCH_DATA,
  SET_BANKING_APPLICATION,
  SET_BANKING_SECTION,
  SET_POST_SUBMISSION_STEP,
  SET_BANKING_SUBMISSION_ERROR,
  SET_PATCH_FULL_APPLICATION_ERROR,
} from './actionTypes';
import {
  Applicant,
  ApplicationDocument,
  BankingApplicant,
  BankingApplicationDocument,
  DO_APPLICATION_TYPE,
  LegalText,
} from 'client-experience-toolbox';
import { DOTCheckResponse, BranchData } from './types';

export interface ApplicationState {
  factoringApplication: Partial<ApplicationDocument>;
  factoringSection: number;
  bankingSection: number;
  bankingApplication: Partial<BankingApplicationDocument>;
  sectionHasChanged: boolean;
  verifiedDOT?: string;
  legalTexts?: LegalText[];
  language?: string;
  submissionStatus?: 'submitting' | 'submitted' | 'failed';
  dotCheckResponse?: DOTCheckResponse;
  branchData?: BranchData;
  submissionStep: number;
  bankingSubmissionError: boolean;
  patchFullApplicationError: boolean;
}

export const defaultAppState: ApplicationState = {
  factoringApplication: {},
  factoringSection: -1,
  bankingSection: -1,
  bankingApplication: {},
  sectionHasChanged: false,
  verifiedDOT: undefined,
  submissionStatus: undefined,
  dotCheckResponse: undefined,
  branchData: undefined,
  submissionStep: 1,
  bankingSubmissionError: false,
  patchFullApplicationError: false,
};

export default (state = defaultAppState, action: AppActionType): ApplicationState => {
  switch (action.type) {
    case SET_FACTORING_APPLICATION: {
      return {
        ...state,
        factoringApplication: action.factoringApplication,
        sectionHasChanged: action.sectionHasChanged,
      };
    }
    case SET_FACTORING_SECTION: {
      return {
        ...state,
        factoringSection: action.factoringSection,
        sectionHasChanged: false,
        submissionStatus: undefined,
      };
    }
    case SET_VERIFIED_DOT: {
      return { ...state, verifiedDOT: action.verifiedDOT };
    }
    case ADD_ADDITIONAL_OWNER: {
      switch (action.applicationType) {
        case DO_APPLICATION_TYPE.BANKING: {
          const newApplicant: Partial<BankingApplicant> = {
            key: action.ownerKey,
          };

          const updatedApplicants = state.bankingApplication?.applicationData?.applicants
            ? [...state.bankingApplication.applicationData.applicants, newApplicant]
            : [newApplicant];

          return {
            ...state,
            bankingApplication: {
              ...state.bankingApplication,
              applicationData: {
                ...state.bankingApplication?.applicationData,
                applicants: updatedApplicants,
              },
            },
            sectionHasChanged: true,
          };
        }
        case DO_APPLICATION_TYPE.FACTORING: {
          const newApplicant: Partial<Applicant> = {
            key: action.ownerKey,
          };
          const updatedApplicants = state.factoringApplication?.applicationData?.applicants
            ? [...state.factoringApplication.applicationData.applicants, newApplicant]
            : [newApplicant];

          return {
            ...state,
            factoringApplication: {
              ...state.factoringApplication,
              applicationData: {
                ...state.factoringApplication?.applicationData,
                applicants: updatedApplicants,
              },
            },
            sectionHasChanged: true,
          };
        }
        default:
          return state;
      }
    }
    case REMOVE_ADDITIONAL_OWNER: {
      switch (action.applicationType) {
        case DO_APPLICATION_TYPE.BANKING: {
          const updatedApplicants = state.bankingApplication?.applicationData?.applicants?.filter(
            (applicant) => applicant.key !== action.owner.key,
          );
          return {
            ...state,
            bankingApplication: {
              ...state.bankingApplication,
              applicationData: {
                ...state.bankingApplication?.applicationData,
                applicants: updatedApplicants,
              },
            },
            sectionHasChanged: true,
          };
        }
        case DO_APPLICATION_TYPE.FACTORING: {
          const updatedApplicants = state.factoringApplication?.applicationData?.applicants?.filter(
            (applicant) => applicant.key !== action.owner.key,
          );
          return {
            ...state,
            factoringApplication: {
              ...state.factoringApplication,
              applicationData: {
                ...state.factoringApplication?.applicationData,
                applicants: updatedApplicants,
              },
            },
            sectionHasChanged: true,
          };
        }
        default:
          return state;
      }
    }
    case SET_LEGAL_TEXTS: {
      return {
        ...state,
        legalTexts: action.legalTexts,
        language: action.language,
      };
    }
    case SET_SUBMISSION_STATUS: {
      return {
        ...state,
        submissionStatus: action.submissionStatus,
      };
    }
    case SET_DOT_CHECK_RESPONSE: {
      return {
        ...state,
        dotCheckResponse: action.dotCheckResponse,
      };
    }
    case SET_BRANCH_DATA: {
      return { ...state, branchData: action.branchData };
    }
    case SET_BANKING_APPLICATION: {
      return {
        ...state,
        bankingApplication: action.bankingApplication,
        sectionHasChanged: action.sectionHasChanged,
      };
    }
    case SET_BANKING_SECTION: {
      return {
        ...state,
        bankingSection: action.bankingSection,
        sectionHasChanged: false,
      };
    }

    case SET_POST_SUBMISSION_STEP: {
      return {
        ...state,
        submissionStep: action.submissionStep,
      };
    }
    case SET_BANKING_SUBMISSION_ERROR: {
      return {
        ...state,
        bankingSubmissionError: action.bankingSubmissionError,
      };
    }
    case SET_PATCH_FULL_APPLICATION_ERROR: {
      return {
        ...state,
        patchFullApplicationError: action.patchFullApplicationError,
      };
    }
    default:
      return state;
  }
};
